import React from 'react';
import classNames from 'classnames';
import { LayoutGrid } from 'lucide-react';
import { NamedLink } from '../../../components';
import { getCategoryIcon } from './categoryIcons';
import { filterCategoriesByVisibility } from '../../../util/categoryVisibility';
import css from './CategoryNavStrip.module.css';

/**
 * CategoryNavStrip — Apple Mac-page-inspired category navigation.
 *
 * On `/s`: horizontal icon cards for all top-level categories.
 * On `/c/phones`: heading + subcategory icon cards + "Browse by type" pill tabs.
 *
 * No scroll arrows — pure touch/trackpad scroll like Apple's Mac page.
 */
const CategoryNavStrip = ({
  categories = [],
  activeCategory1,
  activeCategory2,
  activeCategory,
  categoryVisibility = {},
}) => {
  const isOnCategoryPage = !!activeCategory1;
  const subcategories = activeCategory?.subcategories || [];
  const visibleCategories = filterCategoriesByVisibility(categories, categoryVisibility);
  const visibleSubcategories = filterCategoriesByVisibility(subcategories, categoryVisibility);

  // ── Main search page: show top-level category cards ────
  if (!isOnCategoryPage) {
    if (!visibleCategories.length) {
      return null;
    }

    return (
      <nav className={css.root} aria-label="Category navigation">
        <div className={css.inner}>
          <div className={css.carousel}>
            {visibleCategories.map(cat => {
              const Icon = getCategoryIcon(cat.id);
              const isActive = activeCategory1 === cat.id;
              return (
                <NamedLink
                  key={cat.id}
                  name="CategoryPage1"
                  params={{ category1: cat.id }}
                  className={classNames(css.card, { [css.cardActive]: isActive })}
                >
                  <span className={classNames(css.iconWrap, { [css.iconWrapActive]: isActive })}>
                    <Icon size={36} strokeWidth={1.5} />
                  </span>
                  <span className={css.cardLabel}>{cat.name}</span>
                </NamedLink>
              );
            })}
            <NamedLink
              name="SearchPage"
              className={classNames(css.card, css.viewAllCard)}
            >
              <span className={css.iconWrap}>
                <LayoutGrid size={36} strokeWidth={1.5} />
              </span>
              <span className={css.cardLabel}>View All</span>
            </NamedLink>
          </div>
        </div>
      </nav>
    );
  }

  // ── Category page: pill tabs only ────
  const categoryName = activeCategory?.name || activeCategory1;

  if (!visibleSubcategories.length) return null;

  return (
    <nav className={css.root} aria-label="Category navigation">
      <div className={css.inner}>
        <div className={css.pillSection}>
          <h3 className={css.pillHeading}>Browse by type.</h3>
          <div className={css.pills}>
            <NamedLink
              name="CategoryPage1"
              params={{ category1: activeCategory1 }}
              className={classNames(css.pill, { [css.pillActive]: !activeCategory2 })}
            >
              All {categoryName}
            </NamedLink>
            {visibleSubcategories.map(sub => {
              const isActive = activeCategory2 === sub.id;
              return (
                <NamedLink
                  key={sub.id}
                  name="CategoryPage2"
                  params={{ category1: activeCategory1, category2: sub.id }}
                  className={classNames(css.pill, { [css.pillActive]: isActive })}
                >
                  {sub.name}
                </NamedLink>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default CategoryNavStrip;
